import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { setToken, clearToken, getToken } from '../lib/api';
import { clearAllConversationKeys } from '../lib/agentSession';

const AuthContext = createContext(null);

// ─── Timing constants ─────────────────────────────────────────────────────────
// Strategy: ping /api/health (fast, no auth) until server responds, THEN hit
// /auth/me.  This prevents three 25-second timeouts stacking during a Railway
// cold start / redeploy — the health ping is cheap and retries quickly.

const HEALTH_TIMEOUT  = 6000;   // 6 s per health ping attempt
const HEALTH_INTERVAL = 5000;   // retry health ping every 5 s while server is down
const HEALTH_MAX_WAIT = 120000; // give up after 2 min total (user can reload)

const AUTH_ME_TIMEOUT = 15000;  // 15 s per /auth/me call
const AUTH_ME_RETRIES = 2;      // retry /auth/me up to 2× after a transient error

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Ping /api/health until it responds 200 or maxWait expires.
 *  Returns true if alive, false if we timed out completely. */
async function waitForServer(onProgress) {
  const start = Date.now();
  let attempt = 0;
  while (Date.now() - start < HEALTH_MAX_WAIT) {
    attempt++;
    try {
      // api baseURL is already /api, so '/health' → /api/health
      await api.get('/health', { timeout: HEALTH_TIMEOUT });
      return true; // server is up
    } catch {
      // Server not ready yet — report progress and wait before retrying
      const elapsed = Date.now() - start;
      onProgress && onProgress(elapsed, attempt);
      await new Promise(r => setTimeout(r, HEALTH_INTERVAL));
    }
  }
  return false; // gave up
}

/** Call /auth/me with retries on transient errors (timeouts, 5xx).
 *  Returns user data on success, throws on definitive 401/403 or all retries failed. */
async function fetchAuthMe() {
  let lastErr;
  for (let i = 1; i <= AUTH_ME_RETRIES; i++) {
    try {
      const res = await api.get('/auth/me', { timeout: AUTH_ME_TIMEOUT });
      return res.data;
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      if (status === 401 || status === 403) throw err; // definitive — don't retry
      if (i < AUTH_ME_RETRIES) await new Promise(r => setTimeout(r, 1500));
    }
  }
  throw lastErr;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  // BB-080: If there's no token in localStorage at mount time, we already know
  // the visitor is logged out. Start with loading=false so the LandingPage
  // renders on the very first frame — no dark LoadingScreen flash for visitors.
  const hasTokenAtMount = useRef(!!getToken());
  const [loading, setLoading] = useState(hasTokenAtMount.current);
  // healthMs: milliseconds spent waiting for server health (drives loading UI)
  const [healthMs, setHealthMs] = useState(0);
  const [demoViewAs, setDemoViewAs] = useState(null);

  const checkAuth = useCallback(async () => {
    // If there's no token at all, skip everything — go straight to login screen
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // ── Step 1: Make sure the server is alive ──────────────────────────────
      // Only ping health if the server seems unresponsive (first try is free).
      // We do a single fast probe first; if it fails we enter the retry loop.
      let serverAlive = false;
      try {
        await api.get('/health', { timeout: HEALTH_TIMEOUT });
        serverAlive = true;
      } catch {
        // Server didn't respond instantly — start the polling loop
        setHealthMs(1); // trigger "waking up" UI immediately
        serverAlive = await waitForServer((elapsed) => setHealthMs(elapsed));
      }

      if (!serverAlive) {
        // Complete timeout — clear token so user sees login with an explanation
        clearToken();
        setUser(null);
        return;
      }

      // Reset health timer once server is confirmed alive
      setHealthMs(0);

      // ── Step 2: Authenticate ───────────────────────────────────────────────
      const userData = await fetchAuthMe();
      setUser(userData);
    } catch (err) {
      // 401/403 = not logged in; other errors = let them through to login page
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    const handler = () => { setUser(null); clearToken(); };
    window.addEventListener('st:unauthorized', handler);
    return () => window.removeEventListener('st:unauthorized', handler);
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const res = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    if (res.data.requires_2fa) return { requires2fa: true, email };
    setToken(res.data.token);
    setUser(res.data.user);
    setDemoViewAs(null);
    return { success: true };
  };

  const register = async (email, password, name, metaFields = {}) => {
    const res = await api.post('/auth/register', { email, password, name, ...metaFields });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const verify2fa = async (email, code) => {
    const res = await api.post('/auth/verify-2fa', { email, code });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    // CHAT-HIST-1: drop every saved chat key before the signed-in user is cleared.
    clearAllConversationKeys();
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearToken();
    setUser(null);
    setDemoViewAs(null);
  };

  const effectiveAccountType = (user?.is_demo && demoViewAs)
    ? demoViewAs
    : user?.account_type;

  const effectiveUser = user
    ? { ...user, account_type: effectiveAccountType || user.account_type }
    : null;

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      rawUser: user,
      loading,
      healthMs,   // exposed so LoadingScreen can show accurate "waking up" progress
      login, register, verify2fa, logout, checkAuth,
      demoViewAs,
      setDemoViewAs,
      isDemo: !!user?.is_demo,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
